import { ArrowUp, ArrowUpRight, Instagram, Linkedin, Mail, MessageCircle, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { navLinks, WA_DEFAULT, BRAND } from "../../lib/site";
import { scrollToId, scrollToTop } from "../../lib/scroll";
import { PixelMark } from "./Nav";

export const Footer = () => (
  <footer data-testid="site-footer" className="relative border-t hairline bg-panel/40 overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-28 md:pb-16">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Link to="/" data-testid="footer-logo" aria-label="STUZIO. EDITFLOW home">
            <img src="/assets/logo-dark.png" alt="STUZIO. EDITFLOW — Smarter Marketing. Better Results." className="h-28 w-auto object-contain" />
          </Link>
          <p className="mt-5 font-display italic font-light text-2xl text-white/85">{BRAND.tagline}</p>
          <p className="mt-4 text-mute text-sm leading-relaxed max-w-sm">
            A website development company in India crafting conversion-first websites for small businesses —
            from Varanasi to every corner of the country.
          </p>
          <div className="mt-6 flex gap-3">
            {[
              { icon: Instagram, label: "Instagram", href: "https://instagram.com", testid: "footer-social-instagram" },
              { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com", testid: "footer-social-linkedin" },
              { icon: MessageCircle, label: "WhatsApp", href: WA_DEFAULT, testid: "footer-social-whatsapp" },
              { icon: Mail, label: "Email", href: `mailto:${BRAND.email}`, testid: "footer-social-email" },
            ].map((s) => (
              <a
                key={s.label}
                data-testid={s.testid}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-10 h-10 border hairline flex items-center justify-center text-mute hover:text-brand hover:border-brand/50 transition-all duration-300"
              >
                <s.icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-brand mb-6">Navigate</p>
          <ul className="space-y-3.5">
            {navLinks.map((l) => (
              <li key={l.label}>
                <button
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  onClick={() => scrollToId(l.href)}
                  className="text-mute hover:text-white text-sm transition-colors duration-300 inline-flex items-center gap-1.5 group"
                >
                  {l.label}
                  <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 text-brand transition-opacity duration-300" />
                </button>
              </li>
            ))}
            <li>
              <Link data-testid="footer-link-contact-page" to="/contact" className="text-mute hover:text-white text-sm transition-colors duration-300 inline-flex items-center gap-1.5 group">
                Contact Page
                <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 text-brand transition-opacity duration-300" />
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-brand mb-6">Reach Me</p>
          <ul className="space-y-4 text-sm">
            <li>
              <a data-testid="footer-email-link" href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-3 text-mute hover:text-brand transition-colors duration-300">
                <Mail size={15} className="text-brand" /> {BRAND.email}
              </a>
            </li>
            <li>
              <a data-testid="footer-whatsapp-link" href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-mute hover:text-brand transition-colors duration-300">
                <MessageCircle size={15} className="text-brand" /> {BRAND.phoneDisplay}
              </a>
            </li>
            <li className="inline-flex items-center gap-3 text-mute">
              <MapPin size={15} className="text-brand" /> {BRAND.location}
            </li>
          </ul>
          <button
            data-testid="footer-back-to-top"
            onClick={scrollToTop}
            className="group mt-8 inline-flex items-center gap-3 pl-5 pr-1.5 py-1.5 border hairline text-mute hover:text-white hover:border-brand/50 text-xs font-semibold tracking-widest uppercase transition-all duration-300"
          >
            Back to top
            <span className="btn-arrow-box !w-8 !h-8"><ArrowUp size={13} /></span>
          </button>
        </div>
      </div>

      <p className="mt-16 font-display font-light text-[13vw] leading-none text-white/[0.045] select-none whitespace-nowrap" aria-hidden="true">
        STUZIO. EDITFLOW
      </p>

      <div className="mt-4 pt-6 border-t hairline flex flex-wrap items-center justify-between gap-3">
        <p className="text-mute text-xs">© 2026 {BRAND.name}. All rights reserved.</p>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-mute">Designed &amp; built by {BRAND.founder} in Varanasi</p>
      </div>
    </div>
  </footer>
);
